import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { FormField, FormToggle, ColorPickerField } from '@/shared/components'
import type { TypeActivite } from '@/shared/types/admin'

const schema = z.object({
  code: z.string().min(1, 'Le code est obligatoire'),
  libelle: z.string().min(1, 'Le libellé est obligatoire'),
  couleur: z.string().optional(),
  actif: z.boolean(),
})

type FormValues = z.infer<typeof schema>

interface TypeActiviteFormProps {
  typeActivite?: TypeActivite
  onSubmit: (data: { code: string; libelle: string; couleur?: string; actif: boolean }) => void
  isPending: boolean
}

export function TypeActiviteForm({ typeActivite, onSubmit, isPending }: TypeActiviteFormProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { code: '', libelle: '', couleur: '', actif: true },
  })

  useEffect(() => {
    if (typeActivite) {
      setValue('code', typeActivite.code)
      setValue('libelle', typeActivite.libelle)
      setValue('couleur', typeActivite.couleur || '')
      setValue('actif', typeActivite.actif)
    }
  }, [typeActivite, setValue])

  const handleFormSubmit = (values: FormValues) => {
    onSubmit({
      code: values.code.trim(),
      libelle: values.libelle.trim(),
      couleur: values.couleur || undefined,
      actif: values.actif,
    })
  }

  const inputClass =
    'h-10 w-full rounded-lg border border-neutral-300 bg-neutral-50 px-3 text-sm transition-colors hover:border-neutral-400 focus:border-primary focus:bg-white focus:outline-none focus:ring-2 focus:ring-primary/25'

  return (
    <form onSubmit={handleSubmit(handleFormSubmit)} className="mx-auto max-w-lg space-y-6">
      <div className="rounded-xl border border-neutral-200 bg-white p-6 space-y-5">
        <h2 className="text-base font-semibold text-neutral-900">
          {t('administration.sections.activities.form.informationTitle', "Informations de l'activité")}
        </h2>

        <FormField
          label={t('administration.sections.activities.form.label', 'Libellé')}
          error={errors.libelle?.message}
          required
        >
          <input {...register('libelle')} className={inputClass} />
        </FormField>

        <FormField
          label={t('administration.sections.activities.form.code', 'Code')}
          error={errors.code?.message}
          helper={t('administration.sections.activities.form.codeHelper', 'Code unique (ex: REVISIONS, ECOLE, LOISIRS)')}
          required
        >
          <input {...register('code')} className={inputClass} />
        </FormField>

        <ColorPickerField
          label={t('administration.sections.activities.form.color', 'Couleur')}
          value={watch('couleur') || ''}
          onChange={(v) => setValue('couleur', v)}
        />

        <FormToggle
          label={t('administration.sections.activities.form.active', 'Activité active')}
          checked={watch('actif')}
          onChange={(v) => setValue('actif', v)}
        />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={() => navigate('/administration/types-activite')}
          className="rounded-lg border border-neutral-300 px-4 py-2.5 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-50"
        >
          {t('administration.sections.activities.form.cancel', 'Annuler')}
        </button>
        <button
          type="submit"
          disabled={isPending}
          className="rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary/90 disabled:opacity-50"
        >
          {isPending
            ? t('administration.sections.activities.form.saving', 'Enregistrement...')
            : typeActivite
              ? t('administration.sections.activities.form.save', 'Enregistrer')
              : t('administration.sections.activities.form.create', "Créer l'activité")}
        </button>
      </div>
    </form>
  )
}
